import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
    useAppDispatch,
    useAppSelector,
    ticketInfoSelector,
    useGetTicketQuery,
    setTicketInfo,
    setTicketDepartment,
    setTicketStatus,
    setTicketPriority,
    setTicketLoading,
    setTicketTotalPages,
    setTicketTitle,
    setTicketId,
} from '../../../../app/store';

const MIN_SWITCH_LOADING_MS = 260;

const useTicketItem = () => {
    const dispatch = useAppDispatch();
    const ticketInfo = useAppSelector(ticketInfoSelector);
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const activeTicketIdRef = useRef<string | undefined>();
    const loadingStartedAtRef = useRef(0);
    const finishLoadingTimerRef = useRef<number | null>(null);

    const {
        data: ticketData,
        isFetching: isLoadingTicket,
        isSuccess: isSuccessTicket,
        isError: isTicketError,
        error: ticketError,
        refetch: refetchTicketInfo,
    } = useGetTicketQuery(Number(ticketId), { skip: !ticketId });

    const clearFinishLoadingTimer = () => {
        if (finishLoadingTimerRef.current) {
            window.clearTimeout(finishLoadingTimerRef.current);
            finishLoadingTimerRef.current = null;
        }
    };

    const finishLoading = () => {
        clearFinishLoadingTimer();

        const elapsed = Date.now() - loadingStartedAtRef.current;
        const remaining = Math.max(0, MIN_SWITCH_LOADING_MS - elapsed);

        finishLoadingTimerRef.current = window.setTimeout(() => {
            dispatch(setTicketLoading(false));
            finishLoadingTimerRef.current = null;
        }, remaining);
    };

    useEffect(() => {
        if (activeTicketIdRef.current === ticketId) return;

        activeTicketIdRef.current = ticketId;
        clearFinishLoadingTimer();

        if (!ticketId) {
            dispatch(setTicketLoading(false));
            return;
        }

        loadingStartedAtRef.current = Date.now();
        dispatch(setTicketLoading(true));
        dispatch(setTicketInfo(undefined));
    }, [dispatch, ticketId]);

    useEffect(() => {
        if (!ticketId || !isTicketError) return;

        const errorStatus = Number((ticketError as any)?.status ?? 0);

        if (errorStatus === 403 || errorStatus === 404) {
            clearFinishLoadingTimer();
            dispatch(setTicketInfo(undefined));
            dispatch(setTicketTitle(undefined));
            dispatch(setTicketId(undefined));
            dispatch(setTicketStatus(undefined));
            dispatch(setTicketDepartment(undefined));
            dispatch(setTicketPriority(undefined));
            dispatch(setTicketTotalPages(1));
            dispatch(setTicketLoading(false));

            const ticketBasePath = location.pathname.startsWith(
                '/register/ticket',
            )
                ? '/register/ticket'
                : '/ticket';

            navigate(ticketBasePath, { replace: true });
            return;
        }

        finishLoading();
    }, [
        dispatch,
        isTicketError,
        location.pathname,
        navigate,
        ticketError,
        ticketId,
    ]);

    useEffect(() => {
        if (!isSuccessTicket || !ticketData) return;

        // RTK Query may resolve a cached ticket immediately. Keep the switch
        // skeleton visible for a short minimum duration so changing tickets is
        // always perceptible, while still waiting normally on slower requests.
        dispatch(setTicketInfo(ticketData));
        dispatch(setTicketTitle(ticketData.title));
        dispatch(setTicketId(ticketData.id));
        dispatch(setTicketStatus(ticketData.status));
        dispatch(setTicketDepartment(ticketData.department));
        dispatch(setTicketPriority(ticketData.priority));
        dispatch(
            setTicketTotalPages(
                ticketData.message?.pagination?.total_pages ?? 1,
            ),
        );

        finishLoading();
    }, [dispatch, isSuccessTicket, ticketData]);

    useEffect(() => {
        if (isLoadingTicket) {
            dispatch(setTicketLoading(true));
        }
    }, [dispatch, isLoadingTicket]);

    useEffect(() => {
        return () => {
            clearFinishLoadingTimer();
        };
    }, []);

    return {
        ticketInfo,
        onRefetchInfo: refetchTicketInfo,
    };
};

export default useTicketItem;
